import React from 'react';
import QRCode from 'qrcode';

/**
 * EAN-13 Barcode SVG Generator
 * Renders standard EAN-13 layout with extended guard bars, left first-digit,
 * middle & right digit groups, and right quiet zone '>' symbol.
 */
const L_CODES: Record<string, string> = {
  '0': '0001101', '1': '0011001', '2': '0010011', '3': '0111101', '4': '0100011',
  '5': '0110001', '6': '0101111', '7': '0111011', '8': '0110111', '9': '0001011'
};

const G_CODES: Record<string, string> = {
  '0': '0100111', '1': '0110011', '2': '0011011', '3': '0100001', '4': '0011101',
  '5': '0111001', '6': '0000101', '7': '0010001', '8': '0001001', '9': '0010111'
};

const R_CODES: Record<string, string> = {
  '0': '1110010', '1': '1100110', '2': '1101100', '3': '1000010', '4': '1011100',
  '5': '1001110', '6': '1010000', '7': '1000100', '8': '1001000', '9': '1110100'
};

const PARITY_MAP: Record<string, string[]> = {
  '0': ['L','L','L','L','L','L'],
  '1': ['L','L','G','L','G','G'],
  '2': ['L','L','G','G','L','G'],
  '3': ['L','L','G','G','G','L'],
  '4': ['L','G','L','L','G','G'],
  '5': ['L','G','G','L','L','G'],
  '6': ['L','G','G','G','L','L'],
  '7': ['L','G','L','G','L','G'],
  '8': ['L','G','L','G','G','L'],
  '9': ['L','G','G','L','G','L']
};

export interface BarcodeProps {
  value: string;
  color?: string;
  backgroundColor?: string;
  isTransparentBg?: boolean;
  showText?: boolean;
  width?: number;
  height?: number;
}

export const Ean13BarcodeSvg: React.FC<BarcodeProps> = ({
  value,
  color = '#000000',
  backgroundColor = '#ffffff',
  isTransparentBg = false,
  showText = true,
  width = 160,
  height = 80
}) => {
  // Ensure value is digits only and 13 characters
  let cleanValue = String(value || '').replace(/\D/g, '');
  if (cleanValue.length < 13) {
    cleanValue = cleanValue.padStart(13, '5901234123457').slice(0, 13);
  } else if (cleanValue.length > 13) {
    cleanValue = cleanValue.slice(0, 13);
  }

  const d0 = cleanValue[0];
  const leftDigits = cleanValue.slice(1, 7);
  const rightDigits = cleanValue.slice(7, 13);
  const parity = PARITY_MAP[d0] || PARITY_MAP['0'];

  // Build module sequence
  const modules: { bit: number; isGuard: boolean }[] = [];

  // Left guard: 101
  modules.push({ bit: 1, isGuard: true });
  modules.push({ bit: 0, isGuard: true });
  modules.push({ bit: 1, isGuard: true });

  // Left group (6 digits)
  for (let i = 0; i < 6; i++) {
    const digit = leftDigits[i] || '0';
    const mode = parity[i];
    const pattern = mode === 'G' ? G_CODES[digit] : L_CODES[digit];
    for (let b = 0; b < 7; b++) {
      modules.push({ bit: parseInt(pattern[b], 10), isGuard: false });
    }
  }

  // Center guard: 01010
  modules.push({ bit: 0, isGuard: true });
  modules.push({ bit: 1, isGuard: true });
  modules.push({ bit: 0, isGuard: true });
  modules.push({ bit: 1, isGuard: true });
  modules.push({ bit: 0, isGuard: true });

  // Right group (6 digits)
  for (let i = 0; i < 6; i++) {
    const digit = rightDigits[i] || '0';
    const pattern = R_CODES[digit];
    for (let b = 0; b < 7; b++) {
      modules.push({ bit: parseInt(pattern[b], 10), isGuard: false });
    }
  }

  // Right guard: 101
  modules.push({ bit: 1, isGuard: true });
  modules.push({ bit: 0, isGuard: true });
  modules.push({ bit: 1, isGuard: true });

  // Dimensions
  const quietLeft = 10;
  const quietRight = 10;
  const totalModules = modules.length; // 95
  const moduleWidth = Math.max(1, (width - quietLeft - quietRight) / totalModules);
  const totalBarcodeWidth = totalModules * moduleWidth;
  const startX = quietLeft;

  const barHeight = showText ? Math.max(20, height - 20) : height;
  const guardBarHeight = showText ? barHeight + 8 : barHeight;

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ backgroundColor: isTransparentBg ? 'transparent' : backgroundColor }}
    >
      {!isTransparentBg && (
        <rect x={0} y={0} width={width} height={height} fill={backgroundColor} />
      )}

      {/* Render Bars */}
      {modules.map((m, idx) => {
        if (m.bit === 0) return null;
        const x = startX + idx * moduleWidth;
        const h = m.isGuard ? guardBarHeight : barHeight;
        return (
          <rect
            key={idx}
            x={x}
            y={0}
            width={moduleWidth * 1.05} // slight overlap to prevent subpixel gaps
            height={h}
            fill={color}
          />
        );
      })}

      {/* Render Numbers */}
      {showText && (
        <g fill={color} fontFamily="monospace, sans-serif" fontSize={Math.min(14, Math.max(10, height * 0.22))} fontWeight="600">
          {/* First digit (far left outside left guard) */}
          <text x={startX - 6} y={height - 2} textAnchor="end">
            {d0}
          </text>

          {/* Left 6 digits group */}
          <text
            x={startX + 3 * moduleWidth + (42 * moduleWidth) / 2}
            y={height - 2}
            textAnchor="middle"
            letterSpacing={moduleWidth * 0.5}
          >
            {leftDigits}
          </text>

          {/* Right 6 digits group */}
          <text
            x={startX + 50 * moduleWidth + (42 * moduleWidth) / 2}
            y={height - 2}
            textAnchor="middle"
            letterSpacing={moduleWidth * 0.5}
          >
            {rightDigits}
          </text>

          {/* Quiet zone '>' on far right */}
          <text x={startX + totalBarcodeWidth + 4} y={height - 2} textAnchor="start">
            &gt;
          </text>
        </g>
      )}
    </svg>
  );
};

export const UpcaBarcodeSvg: React.FC<BarcodeProps> = ({
  value,
  color = '#000000',
  backgroundColor = '#ffffff',
  isTransparentBg = false,
  showText = true,
  width = 160,
  height = 80
}) => {
  // UPC-A has 12 digits
  let cleanValue = String(value || '').replace(/\D/g, '');
  if (cleanValue.length < 12) {
    cleanValue = cleanValue.padStart(12, '012345678905').slice(0, 12);
  } else if (cleanValue.length > 12) {
    cleanValue = cleanValue.slice(0, 12);
  }

  const systemDigit = cleanValue[0];
  const leftGroup = cleanValue.slice(1, 6);
  const rightGroup = cleanValue.slice(6, 11);
  const checkDigit = cleanValue[11];

  // Module construction
  const modules: { bit: number; isGuard: boolean }[] = [];

  // Left guard: 101
  modules.push({ bit: 1, isGuard: true });
  modules.push({ bit: 0, isGuard: true });
  modules.push({ bit: 1, isGuard: true });

  // System digit + Left 5 digits (6 digits total in L-codes)
  for (let i = 0; i < 6; i++) {
    const digit = cleanValue[i];
    const pattern = L_CODES[digit] || L_CODES['0'];
    for (let b = 0; b < 7; b++) {
      modules.push({ bit: parseInt(pattern[b], 10), isGuard: false });
    }
  }

  // Center guard: 01010
  modules.push({ bit: 0, isGuard: true });
  modules.push({ bit: 1, isGuard: true });
  modules.push({ bit: 0, isGuard: true });
  modules.push({ bit: 1, isGuard: true });
  modules.push({ bit: 0, isGuard: true });

  // Right 5 digits + Check digit (6 digits total in R-codes)
  for (let i = 6; i < 12; i++) {
    const digit = cleanValue[i];
    const pattern = R_CODES[digit] || R_CODES['0'];
    for (let b = 0; b < 7; b++) {
      modules.push({ bit: parseInt(pattern[b], 10), isGuard: false });
    }
  }

  // Right guard: 101
  modules.push({ bit: 1, isGuard: true });
  modules.push({ bit: 0, isGuard: true });
  modules.push({ bit: 1, isGuard: true });

  const quietLeft = 10;
  const quietRight = 10;
  const totalModules = modules.length; // 95
  const moduleWidth = Math.max(1, (width - quietLeft - quietRight) / totalModules);
  const totalBarcodeWidth = totalModules * moduleWidth;
  const startX = quietLeft;

  const barHeight = showText ? Math.max(20, height - 20) : height;
  const guardBarHeight = showText ? barHeight + 8 : barHeight;

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ backgroundColor: isTransparentBg ? 'transparent' : backgroundColor }}
    >
      {!isTransparentBg && (
        <rect x={0} y={0} width={width} height={height} fill={backgroundColor} />
      )}

      {modules.map((m, idx) => {
        if (m.bit === 0) return null;
        const x = startX + idx * moduleWidth;
        const h = m.isGuard ? guardBarHeight : barHeight;
        return (
          <rect
            key={idx}
            x={x}
            y={0}
            width={moduleWidth * 1.05}
            height={h}
            fill={color}
          />
        );
      })}

      {showText && (
        <g fill={color} fontFamily="monospace, sans-serif" fontSize={Math.min(14, Math.max(10, height * 0.22))} fontWeight="600">
          {/* System Digit on Far Left */}
          <text x={startX - 5} y={height - 2} textAnchor="end">
            {systemDigit}
          </text>

          {/* Left 5 Digits */}
          <text
            x={startX + 10 * moduleWidth + (35 * moduleWidth) / 2}
            y={height - 2}
            textAnchor="middle"
            letterSpacing={moduleWidth * 0.5}
          >
            {leftGroup}
          </text>

          {/* Right 5 Digits */}
          <text
            x={startX + 50 * moduleWidth + (35 * moduleWidth) / 2}
            y={height - 2}
            textAnchor="middle"
            letterSpacing={moduleWidth * 0.5}
          >
            {rightGroup}
          </text>

          {/* Check Digit on Far Right */}
          <text x={startX + totalBarcodeWidth + 5} y={height - 2} textAnchor="start">
            {checkDigit}
          </text>
        </g>
      )}
    </svg>
  );
};

export const DataMatrixSvg: React.FC<BarcodeProps> = ({
  value,
  color = '#000000',
  backgroundColor = '#ffffff',
  isTransparentBg = false,
  width = 80,
  height = 80
}) => {
  // Standard ECC200 Data Matrix 14x14 grid + 1 module Quiet Zone
  const matrixSize = 14; 
  const totalGrid = matrixSize + 2; // 16x16 modules including quiet zone
  const quietZone = 1;

  const grid: boolean[][] = Array.from({ length: matrixSize }, () => Array(matrixSize).fill(false));

  // Solid L-shape alignment pattern (Left col, Bottom row)
  for (let i = 0; i < matrixSize; i++) {
    grid[matrixSize - 1][i] = true; // Bottom row solid
    grid[i][0] = true;              // Left col solid
  }

  // Alternating clock track (Top row, Right col)
  for (let i = 0; i < matrixSize; i++) {
    if (i % 2 === 0) {
      grid[0][i] = true;             // Top row alternating
      grid[i][matrixSize - 1] = true; // Right col alternating
    }
  }

  // Fill inner data region deterministically from value string
  const str = String(value || 'DATAMATRIX');
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }

  for (let r = 1; r < matrixSize - 1; r++) {
    for (let c = 1; c < matrixSize - 1; c++) {
      const charIndex = (r * matrixSize + c) % str.length;
      const charCode = str.charCodeAt(charIndex);
      const bitShift = (r + c + Math.abs(hash)) % 8;
      grid[r][c] = ((charCode >> bitShift) & 1) === 1;
    }
  }

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${totalGrid} ${totalGrid}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ backgroundColor: isTransparentBg ? 'transparent' : backgroundColor }}
    >
      {!isTransparentBg && (
        <rect x={0} y={0} width={totalGrid} height={totalGrid} fill={backgroundColor} />
      )}
      {grid.map((row, r) =>
        row.map((cell, c) => {
          if (!cell) return null;
          return (
            <rect
              key={`${r}-${c}`}
              x={c + quietZone}
              y={r + quietZone}
              width={1.02}
              height={1.02}
              fill={color}
            />
          );
        })
      )}
    </svg>
  );
};

export interface QrCodeProps {
  value: string;
  color?: string;
  backgroundColor?: string;
  isTransparentBg?: boolean;
  errorCorrection?: 'L' | 'M' | 'Q' | 'H';
  style?: 'square' | 'dots' | 'fluid';
  width?: number;
  height?: number;
}

export const QrCodeSvg: React.FC<QrCodeProps> = ({
  value,
  color = '#000000',
  backgroundColor = '#ffffff',
  isTransparentBg = false,
  errorCorrection = 'M',
  style = 'square',
  width = 100,
  height = 100
}) => {
  const qrData = React.useMemo(() => {
    try {
      const ecl = (['L', 'M', 'Q', 'H'].includes(errorCorrection || '') ? errorCorrection : 'M') as 'L' | 'M' | 'Q' | 'H';
      const text = value || 'https://example.com';
      return QRCode.create(text, { errorCorrectionLevel: ecl });
    } catch (e) {
      console.error('QR code generation error:', e);
      return null;
    }
  }, [value, errorCorrection]);

  if (!qrData || !qrData.modules) {
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`}>
        {!isTransparentBg && <rect width={width} height={height} fill={backgroundColor} />}
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fill={color} fontSize="12">
          QR Error
        </text>
      </svg>
    );
  }

  const size = qrData.modules.size;
  const modules = qrData.modules.data; // Uint8Array of size * size

  const isFinderPattern = (r: number, c: number) => {
    if (r < 7 && c < 7) return true; // Top-left
    if (r < 7 && c >= size - 7) return true; // Top-right
    if (r >= size - 7 && c < 7) return true; // Bottom-left
    return false;
  };

  const cells = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      const isDark = modules[r * size + c] === 1;
      if (!isDark) continue;

      const key = `${r}-${c}`;
      if (style === 'dots') {
        if (isFinderPattern(r, c)) {
          cells.push(<rect key={key} x={c} y={r} width={1.02} height={1.02} fill={color} />);
        } else {
          cells.push(<circle key={key} cx={c + 0.5} cy={r + 0.5} r={0.42} fill={color} />);
        }
      } else if (style === 'fluid') {
        if (isFinderPattern(r, c)) {
          cells.push(<rect key={key} x={c + 0.02} y={r + 0.02} width={0.96} height={0.96} rx={0.2} ry={0.2} fill={color} />);
        } else {
          cells.push(<rect key={key} x={c + 0.05} y={r + 0.05} width={0.9} height={0.9} rx={0.35} ry={0.35} fill={color} />);
        }
      } else {
        cells.push(<rect key={key} x={c} y={r} width={1.02} height={1.02} fill={color} />);
      }
    }
  }

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${size} ${size}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ backgroundColor: isTransparentBg ? 'transparent' : backgroundColor }}
    >
      {!isTransparentBg && (
        <rect x={0} y={0} width={size} height={size} fill={backgroundColor} />
      )}
      {cells}
    </svg>
  );
};

export const AztecBarcodeSvg: React.FC<BarcodeProps> = ({
  value,
  color = '#000000',
  backgroundColor = '#ffffff',
  isTransparentBg = false,
  width = 80,
  height = 80
}) => {
  // Compact Aztec 19x19 grid with central 9x9 bullseye finder
  const matrixSize = 19;
  const quietZone = 1;
  const totalGrid = matrixSize + quietZone * 2;
  const center = 9; // 0-indexed center coordinate

  const grid: boolean[][] = Array.from({ length: matrixSize }, () => Array(matrixSize).fill(false));

  // Central Bullseye: concentric squares
  // Center dot: 1x1 at (9,9)
  grid[center][center] = true;
  // Outer layer 1 of bullseye (radius 2): 5x5 square border
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++) {
      if (Math.abs(dx) === 2 || Math.abs(dy) === 2) {
        grid[center + dy][center + dx] = true;
      }
    }
  }
  // Outer layer 2 of bullseye (radius 4): 9x9 square border
  for (let dy = -4; dy <= 4; dy++) {
    for (let dx = -4; dx <= 4; dx++) {
      if (Math.abs(dx) === 4 || Math.abs(dy) === 4) {
        grid[center + dy][center + dx] = true;
      }
    }
  }
  // Orientation indicators (corners of 11x11 perimeter)
  grid[center - 5][center - 5] = true;
  grid[center - 5][center + 5] = true;
  grid[center + 5][center - 5] = true;

  // Fill data layers deterministically from value
  const str = String(value || 'AZTEC2026');
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash = (hash ^ str.charCodeAt(i)) * 0x01000193;
  }

  for (let r = 0; r < matrixSize; r++) {
    for (let c = 0; c < matrixSize; c++) {
      // Don't overwrite the core 11x11 bullseye area
      if (Math.abs(r - center) <= 4 && Math.abs(c - center) <= 4) continue;
      if (Math.abs(r - center) <= 5 && Math.abs(c - center) <= 5 && (r === center - 5 || r === center + 5 || c === center - 5 || c === center + 5)) continue;

      const charIdx = (r * matrixSize + c) % str.length;
      const charCode = str.charCodeAt(charIdx);
      const shift = (r + c + Math.abs(hash)) % 7;
      grid[r][c] = ((charCode >> shift) & 1) === 1;
    }
  }

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${totalGrid} ${totalGrid}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ backgroundColor: isTransparentBg ? 'transparent' : backgroundColor }}
    >
      {!isTransparentBg && (
        <rect x={0} y={0} width={totalGrid} height={totalGrid} fill={backgroundColor} />
      )}
      {grid.map((row, r) =>
        row.map((cell, c) => {
          if (!cell) return null;
          return (
            <rect
              key={`aztec-${r}-${c}`}
              x={c + quietZone}
              y={r + quietZone}
              width={1.02}
              height={1.02}
              fill={color}
            />
          );
        })
      )}
    </svg>
  );
};

export const Pdf417BarcodeSvg: React.FC<BarcodeProps> = ({
  value,
  color = '#000000',
  backgroundColor = '#ffffff',
  isTransparentBg = false,
  width = 140,
  height = 60
}) => {
  // Standard PDF417 layout: 6 rows, start pattern (8 modules), left indicator (17), 3 data columns (17 each), right indicator (17), stop pattern (9 modules)
  const numRows = 6;
  const numCols = 3;
  const codewordWidth = 17;
  const startPatternWidth = 8;
  const stopPatternWidth = 9;
  const indicatorWidth = 17;
  const totalModules = startPatternWidth + indicatorWidth + (numCols * codewordWidth) + indicatorWidth + stopPatternWidth; // 8+17+51+17+9 = 102 modules
  const rowHeight = 8;
  const totalSvgHeight = numRows * rowHeight;

  // Generate deterministic bar widths per row
  const str = String(value || 'PDF417');
  const rowsData: boolean[][] = [];

  for (let r = 0; r < numRows; r++) {
    const rowModules: boolean[] = [];
    // 1. Start pattern: 11111110 (8 modules)
    rowModules.push(true, true, true, true, true, true, true, false);

    // 2. Left Row Indicator: alternating 17-module cluster
    for (let i = 0; i < indicatorWidth; i++) {
      rowModules.push((i + r) % 3 !== 0);
    }

    // 3. Data columns
    for (let c = 0; c < numCols; c++) {
      const charIdx = (r * numCols + c) % str.length;
      const charCode = str.charCodeAt(charIdx);
      for (let bit = 0; bit < codewordWidth; bit++) {
        rowModules.push(((charCode + bit + r * 2) % 4) < 2);
      }
    }

    // 4. Right Row Indicator
    for (let i = 0; i < indicatorWidth; i++) {
      rowModules.push((i + r + 1) % 3 === 0);
    }

    // 5. Stop pattern: 111111101 (9 modules)
    rowModules.push(true, true, true, true, true, true, true, false, true);

    rowsData.push(rowModules);
  }

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${totalModules} ${totalSvgHeight}`}
      preserveAspectRatio="none"
      style={{ backgroundColor: isTransparentBg ? 'transparent' : backgroundColor }}
    >
      {!isTransparentBg && (
        <rect x={0} y={0} width={totalModules} height={totalSvgHeight} fill={backgroundColor} />
      )}
      {rowsData.map((row, rIdx) =>
        row.map((isDark, mIdx) => {
          if (!isDark) return null;
          return (
            <rect
              key={`pdf417-${rIdx}-${mIdx}`}
              x={mIdx}
              y={rIdx * rowHeight}
              width={1.01}
              height={rowHeight}
              fill={color}
            />
          );
        })
      )}
    </svg>
  );
};

